import { lazy } from "react";
import type { RouteConfig, RoutePermissions } from "../config";
import { ROUTES } from "../paths";
import { canManageRoomsUser } from "@/lib/room-access";

const SettingsPage = lazy(() =>
  import("@/pages/settings").then((m) => ({ default: m.SettingsPage }))
);
const AccountPage = lazy(() =>
  import("@/pages/settings/account").then((m) => ({ default: m.AccountPage }))
);
const DeclarationSettingsPage = lazy(() =>
  import("@/pages/settings/declarations").then((m) => ({ default: m.DeclarationSettingsPage }))
);
const PrimaveraComparePage = lazy(() =>
  import("@/pages/settings/primavera-compare-page").then((m) => ({ default: m.PrimaveraComparePage }))
);
const AuditLogPage = lazy(() =>
  import("@/pages/settings/audit").then((m) => ({
    default: m.AuditLogPage,
  }))
);
const MeetingRoomsSettingsPage = lazy(() =>
  import("@/pages/settings/meeting-rooms").then((m) => ({ default: m.MeetingRoomsSettingsPage }))
);
const CompanyProfilePage = lazy(() =>
  import("@/pages/settings/company").then((m) => ({ default: m.CompanyProfilePage }))
);
const HelpPage = lazy(() => import("@/pages/help/help-page"));
const UnitsPage = lazy(() =>
  import("@/pages/settings/master-data/units").then((m) => ({
    default: m.UnitsPage,
  }))
);
const CertificatesPage = lazy(() =>
  import("@/pages/settings/master-data/certificates").then((m) => ({
    default: m.CertificatesPage,
  }))
);
const MeasurementsPage = lazy(() =>
  import("@/pages/settings/master-data/measurements").then((m) => ({
    default: m.MeasurementsPage,
  }))
);
const RawMaterialsPage = lazy(() =>
  import("@/pages/settings/master-data/raw-materials").then((m) => ({
    default: m.RawMaterialsPage,
  }))
);
const RawMaterialDetailPage = lazy(() =>
  import("@/pages/settings/master-data/raw-materials/detail").then((m) => ({
    default: m.RawMaterialDetailPage,
  }))
);
const AccessoriesPage = lazy(() =>
  import("@/pages/settings/master-data/accessories").then((m) => ({
    default: m.AccessoriesPage,
  }))
);
const AccessoryDetailPage = lazy(() =>
  import("@/pages/settings/master-data/accessories/detail").then((m) => ({
    default: m.AccessoryDetailPage,
  }))
);
const MachinesPage = lazy(() =>
  import("@/pages/settings/master-data/machines").then((m) => ({
    default: m.MachinesPage,
  }))
);
const MachineDetailPage = lazy(() =>
  import("@/pages/settings/master-data/machines/detail").then((m) => ({
    default: m.MachineDetailPage,
  }))
);
const ColorsPage = lazy(() =>
  import("@/pages/settings/master-data/colors").then((m) => ({
    default: m.ColorsPage,
  }))
);
const PackagingTypesPage = lazy(() =>
  import("@/pages/settings/master-data/packaging-types").then((m) => ({
    default: m.PackagingTypesPage,
  }))
);
const AiUsagePage = lazy(() =>
  import("@/pages/settings/ai-usage/ai-usage-page").then((m) => ({
    default: m.AiUsagePage,
  }))
);
const EmailAccountsPage = lazy(() =>
  import("@/pages/settings/email-accounts").then((m) => ({
    default: m.EmailAccountsPage,
  }))
);
const EmailAutoReplyPage = lazy(() =>
  import("@/pages/settings/email-auto-reply").then((m) => ({
    default: m.EmailAutoReplyPage,
  }))
);
const PermissionsPage = lazy(() =>
  import("@/pages/settings/permissions/permissions-page").then((m) => ({
    default: m.PermissionsPage,
  }))
);
const NotificationsPage = lazy(() => import("@/pages/notifications/notifications-page").then((m) => ({ default: m.NotificationsPage })));
const FeedPage = lazy(() => import("@/pages/feed/feed-page").then((m) => ({ default: m.FeedPage })));
const SecurityPage = lazy(() => import("@/pages/security/security-page").then((m) => ({ default: m.SecurityPage })));
const VisitsPage = lazy(() => import("@/pages/visits/visits-page").then((m) => ({ default: m.VisitsPage })));

const ADMIN = { access: "admin" as const };
const AUTHENTICATED = { access: "authenticated" as const };
const PRODUCT_READ = { access: "permission" as const, entity: "products", action: "READ" };
// As tabelas-mestre do catálogo (medidas, unidades, certificações, matéria-prima)
// são uma feature à parte de `products`: quem compra o catálogo só para
// identificar o que entra e sai não as preenche. Constante própria porque
// `PRODUCT_READ` continua a servir a ficha do artigo, que fica.
const CATALOGO_BASE_READ = { ...PRODUCT_READ, feature: "artigos.catalogo_base" as const };
const MACHINE_READ = { access: "permission" as const, entity: "machines", action: "READ" };
const ACCESSORY_READ = { access: "permission" as const, entity: "accessories", action: "READ" };
// O mural é uma feature do núcleo: sem ela a rota devolve ao painel. Sem isto,
// quem escrevesse o endereço à mão via a página a dizer que não conseguiu
// carregar — e ficava a saber que ela existe.
const FEED_READ = { access: "permission" as const, entity: "feed", action: "READ", feature: "platform.feed" as const };
// Gerir salas: administradores e quem tem `calendar_events.CREATE` — a mesma
// permissão que o backend exige ao gravar, para o ecrã não abrir a quem depois
// leva 403.
const ROOMS_MANAGE: RoutePermissions = {
  access: "custom",
  customCheck: canManageRoomsUser,
};
const SECURITY_READ = { access: "permission" as const, entity: "security", action: "READ" };

export const settingsRoutes: RouteConfig[] = [
  // Any user
  { path: ROUTES.SETTINGS.MAIN, component: SettingsPage, layout: "dashboard", permissions: AUTHENTICATED },
  { path: ROUTES.SETTINGS.ACCOUNT, component: AccountPage, layout: "dashboard", permissions: AUTHENTICATED },
  { path: ROUTES.SETTINGS.DECLARATIONS, component: DeclarationSettingsPage, layout: "dashboard", permissions: { access: "permission", entity: "declarations", action: "UPDATE" } },

  // Admin tools
  { path: ROUTES.SETTINGS.AUDIT_LOG, component: AuditLogPage, layout: "dashboard", permissions: ADMIN },
  { path: ROUTES.SETTINGS.AI_USAGE, component: AiUsagePage, layout: "dashboard", permissions: { access: "custom", customCheck: (user) => !!user.isSuperuser } },
  { path: ROUTES.SETTINGS.EMAIL_ACCOUNTS, component: EmailAccountsPage, layout: "dashboard", permissions: { access: "permission", entity: "email_accounts", action: "READ" } },
  { path: ROUTES.SETTINGS.EMAIL_AUTO_REPLY, component: EmailAutoReplyPage, layout: "dashboard", permissions: { access: "permission", entity: "email_triage", action: "READ" } },
  { path: ROUTES.SETTINGS.COMPANY, component: CompanyProfilePage, layout: "dashboard", permissions: ADMIN },
  { path: ROUTES.SETTINGS.PRIMAVERA_COMPARE, component: PrimaveraComparePage, layout: "dashboard", permissions: { access: "permission", entity: "attendance", action: "CREATE" } },
  { path: ROUTES.SETTINGS.PERMISSIONS, component: PermissionsPage, layout: "dashboard", permissions: ADMIN },
  { path: ROUTES.SETTINGS.MEETING_ROOMS, component: MeetingRoomsSettingsPage, layout: "dashboard", permissions: ROOMS_MANAGE },

  // Master Data - Each entity has its own READ permission
  { path: ROUTES.SETTINGS.MASTER_DATA.MACHINES.LIST, component: MachinesPage, layout: "dashboard", permissions: MACHINE_READ },
  { path: ROUTES.SETTINGS.MASTER_DATA.MACHINES.DETAIL, component: MachineDetailPage, layout: "dashboard", permissions: MACHINE_READ },
  { path: ROUTES.SETTINGS.MASTER_DATA.RAW_MATERIALS.LIST, component: RawMaterialsPage, layout: "dashboard", permissions: CATALOGO_BASE_READ },
  { path: ROUTES.SETTINGS.MASTER_DATA.RAW_MATERIALS.DETAIL, component: RawMaterialDetailPage, layout: "dashboard", permissions: CATALOGO_BASE_READ },
  { path: ROUTES.SETTINGS.MASTER_DATA.ACCESSORIES.LIST, component: AccessoriesPage, layout: "dashboard", permissions: ACCESSORY_READ },
  { path: ROUTES.SETTINGS.MASTER_DATA.ACCESSORIES.DETAIL, component: AccessoryDetailPage, layout: "dashboard", permissions: ACCESSORY_READ },
  { path: ROUTES.SETTINGS.MASTER_DATA.MEASUREMENTS, component: MeasurementsPage, layout: "dashboard", permissions: CATALOGO_BASE_READ },
  { path: ROUTES.SETTINGS.MASTER_DATA.UNITS, component: UnitsPage, layout: "dashboard", permissions: CATALOGO_BASE_READ },
  { path: ROUTES.SETTINGS.MASTER_DATA.CERTIFICATES, component: CertificatesPage, layout: "dashboard", permissions: CATALOGO_BASE_READ },
  { path: ROUTES.SETTINGS.MASTER_DATA.COLORS, component: ColorsPage, layout: "dashboard", permissions: CATALOGO_BASE_READ },
  { path: ROUTES.SETTINGS.MASTER_DATA.PACKAGING_TYPES, component: PackagingTypesPage, layout: "dashboard", permissions: CATALOGO_BASE_READ },


  // Centro de notificações — de qualquer utilizador autenticado: cada um vê só as suas.
  { path: ROUTES.NOTIFICATIONS, component: NotificationsPage, layout: "dashboard", permissions: AUTHENTICATED },

  // Manual da plataforma — universal. Quem usa a app tem direito a saber como
  // ela funciona; o conteúdo não expõe dados, só explica ecrãs.
  { path: ROUTES.HELP, component: HelpPage, layout: "dashboard", permissions: AUTHENTICATED },

  // Feed da empresa — gate by feed.READ (começa no Boss/admin)
  { path: ROUTES.FEED, component: FeedPage, layout: "dashboard", permissions: FEED_READ },

  // App do Segurança — gate by security.READ. Os quatro separadores da barra de
  // baixo são rotas irmãs servidas pela MESMA página: ela lê o endereço e mostra
  // a vista respectiva. São rotas (e não estado local) para o turno sobreviver a
  // um fecho da app — ver o comentário em paths.ts.
  { path: ROUTES.SECURITY, component: SecurityPage, layout: "dashboard", permissions: SECURITY_READ },
  { path: ROUTES.SECURITY_GATE, component: SecurityPage, layout: "dashboard", permissions: SECURITY_READ },
  { path: ROUTES.SECURITY_INCIDENT, component: SecurityPage, layout: "dashboard", permissions: SECURITY_READ },
  { path: ROUTES.SECURITY_LOG, component: SecurityPage, layout: "dashboard", permissions: SECURITY_READ },

  // Visitas — RH/receção/segurança. Quem visita quem é informação sensível
  // (auditorias, advogados, candidatos): não fica à vista de toda a empresa.
  // Um colaborador que espera alguém pede ao RH para agendar.
  { path: ROUTES.VISITS, component: VisitsPage, layout: "dashboard", permissions: SECURITY_READ },
];
